'use client';

import { faLongArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import React, { useCallback, useState } from 'react';
import { trackEvent } from '../../components/google-tag-manager';
import { useLeadCapture } from '../../components/use-lead-capture';
import { Lead } from '../../types/lead';
import { useCampaignTracker } from './campaign-tracker';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  mobile: '',
  companyName: '',
  teamSize: undefined,
};

type DetailsSetter = (details: Lead['details']) => Lead['details'];

const ContactSalesForm = () => {
  const leadCapture = useLeadCapture();
  const trackingInfo = useCampaignTracker();

  const [state, setState] = useState<{
    details: Lead['details'];
    status: 'idle' | 'loading' | 'success' | 'error';
  }>({
    details: initialState,
    status: 'idle',
  });

  const setDetails = useCallback((cb: DetailsSetter) => {
    setState((s) => ({
      ...s,
      status: 'idle',
      details: cb(s.details),
    }));
  }, []);

  const handleDetailsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;

    setDetails((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (state.status === 'loading') { return; }
    if (!state.details.teamSize) { return; }

    setState((s) => ({ ...s, status: 'loading' }));

    trackEvent({
      event: 'contact_sales',
    });

    void leadCapture.submit({
      type: 'contact-sales',
      details: state.details,
      tracking: trackingInfo,
    }).then(() => {
      setState((s) => ({
        ...s,
        details: initialState,
        status: 'success',
      }));
    }, () => {
      setState((s) => ({ ...s, status: 'error' }));
    });
  };

  const teamOptions = [{
    label: 'Just me',
    value: '1',
  }, {
    label: '2 - 3',
    value: '2-3',
  }, {
    label: '4 - 6',
    value: '4-6',
  }, {
    label: '7 - 10',
    value: '7-10',
  }, {
    label: '11+',
    value: '11+',
  }] as const;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <label className="floating-label">
        <span>First name</span>
        <input required type="text" autoComplete="given-name" placeholder="First name" className="input input-lg w-full" name="firstName" value={state.details.firstName} onChange={handleDetailsChange} />
      </label>
      <label className="floating-label">
        <span>Last name</span>
        <input required type="text" autoComplete="family-name" placeholder="Last name" className="input input-lg w-full" name="lastName" value={state.details.lastName} onChange={handleDetailsChange} />
      </label>
      <label className="floating-label">
        <span>Email address</span>
        <input required type="email" placeholder="Email address" className="input input-lg w-full" name="email" value={state.details.email} onChange={handleDetailsChange} />
      </label>
      <label className="floating-label">
        <span>Phone number</span>
        <input required type="tel" placeholder="Phone number" className="input input-lg w-full" name="mobile" value={state.details.mobile} onChange={handleDetailsChange} />
      </label>
      <label className="floating-label">
        <span>Company name</span>
        <input type="text" autoComplete="organization" placeholder="Company name" className="input input-lg w-full" name="companyName" value={state.details.companyName} onChange={handleDetailsChange} />
      </label>

      <div>
        <p className="mt-8 mb-4 text-center">
          How many team members do you have?
        </p>

        <div className="grid grid-cols-2 gap-4 mb-4">
          {teamOptions.map(({ label, value }) => {
            const selected = state.details.teamSize === value;

            const handlClick = () => {
              setDetails((s) => ({ ...s, teamSize: value }))
            };

            return (
              <button
                key={value}
                type="button"
                className={`btn ${selected ? 'btn-active' : ''}`}
                onClick={handlClick}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {(() => {
        const renderContactSales = () => {
          return (
            <button type="submit" className="btn btn-block btn-primary btn-lg gap-2" disabled={state.status === 'loading'}>
              {state.status === 'loading' ? (
                <span className="loading loading-spinner" />
              ) : (
                <>
                  Contact sales
                  <FontAwesomeIcon icon={faLongArrowRight} />
                </>
              )}
            </button>
          );
        };

        switch (state.status) {
          case 'idle':
          case 'loading':
            return renderContactSales();

          case 'success': return (
            <>
              <div className="mt-8 text-center">
                <p className="font-medium text-center mb-4 text-lg">
                  {`Thanks! We'll be in touch soon.`}
                </p>

                <Link href="/watch-demo/play" className="btn btn-primary btn-wide">
                  Watch the demo
                </Link>
              </div>
            </>
          );

          case 'error': return (
            <>
              {renderContactSales()}

              <div className="bg-white rounded-xl overflow-hidden mt-2">
                <div className="p-3 sm:p-5 bg-error/90 text-white/90 font-bold text-center">
                  Oops! Something went wrong.
                </div>
              </div>
            </>
          );

          default: return null;
        }
      })()}
    </form>
  );
};

export default ContactSalesForm;
